/**
 * @file SelectedVideosList.tsx
 *
 * @description Component for displaying and managing a list of selected videos for police video evidence.
 * This component shows chips for each selected video, allowing officers to review their current selection
 * and remove videos as needed.
 *
 * @module Components
 * @requires React
 * @requires Material-UI
 */

import React from "react";
import { Box, Typography, Chip, Stack, Button, Tooltip, alpha } from "@mui/material";
import {
  Close as CloseIcon,
  ClearAll as ClearAllIcon,
  Share as ShareIcon,
} from "@mui/icons-material";
import { VideoPreview } from "../../shared/interfaces/VideoPreview";
import { MAX_VIDEOS_PER_REPORT } from "./hooks/useVideoSelection";

/**
 * Props interface for the SelectedVideosList component
 * @interface SelectedVideosListProps
 * @property {string[]} selectedVideos - Array of video IDs that are currently selected
 * @property {VideoPreview[]} videos - Array of all available video preview objects
 * @property {function} onRemove - Callback function to handle removal of a video from selection
 */
interface SelectedVideosListProps {
  selectedVideos: string[];
  videos: VideoPreview[];
  onRemove: (videoId: string) => void;
  /** Clears the entire selection. When provided, a "Clear Selection"
   *  action is shown in this box's header. */
  onClear?: () => void;
  /** Opens the share dialog for a single video. When provided together with a
   *  truthy `canShareVideo(id)`, each selected chip becomes clickable to share. */
  onShareVideo?: (videoId: string) => void;
  /** Whether the current user may share a given video; gates the per-chip
   *  share affordance. Defaults to allowing all when omitted. */
  canShareVideo?: (videoId: string) => boolean;
}

/**
 * @name SelectedVideosList
 *
 * @description The component shows the count of selected videos (with a max of 5) and
 * renders each video as a chip with delete capability. If no videos are
 * selected, the component renders nothing.
 *
 * @param {SelectedVideosListProps} props - The component props
 * @param {string[]} props.selectedVideos - Array of selected video IDs
 * @param {VideoPreview[]} props.videos - Complete array of video preview objects
 * @param {function} props.onRemove - Callback function triggered when a video is removed
 *
 * @returns {React.ReactElement | null} The rendered component or null if no videos are selected
 */
const SelectedVideosList: React.FC<SelectedVideosListProps> = ({
  selectedVideos,
  videos,
  onRemove,
  onClear,
  onShareVideo,
  canShareVideo,
}) => {
  // Don't render anything if no videos are selected
  if (selectedVideos.length === 0) return null;

  return (
    <Box
      sx={{
        backgroundColor: "background.paper",
        padding: 2,
        borderRadius: "12px",
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        width: "100%",
        zIndex: 1000,
        border: 1,
        borderColor: "divider",
      }}
    >
      <Stack spacing={2}>
        {/* Header: count on the left, clear-selection action on the right so
            the control lives with the videos it clears. */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Selected Videos ({selectedVideos.length}/{MAX_VIDEOS_PER_REPORT})
          </Typography>
          {onClear && (
            <Tooltip title="Clear all selected videos" arrow>
              <Button
                size="small"
                onClick={onClear}
                startIcon={<ClearAllIcon fontSize="small" />}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  flexShrink: 0,
                  color: "text.secondary",
                  "&:hover": {
                    color: "error.main",
                    backgroundColor: (t) => alpha(t.palette.error.main, 0.04),
                  },
                }}
              >
                Clear Selection
              </Button>
            </Tooltip>
          )}
        </Box>

        {/* Container for video selection chips */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            maxWidth: "100%",
          }}
        >
          {/* Map through selected videos and render a chip for each */}
          {selectedVideos.map((videoId) => {
            // Find the full video object for the current ID
            const video = videos.find((v) => v.id === videoId);

            // Skip rendering if video data not found
            if (!video) return null;

            // A chip is share-clickable when a handler is wired and the user is
            // allowed to share this specific video.
            const shareable =
              !!onShareVideo && (canShareVideo?.(videoId) ?? true);

            const chip = (
              <Chip
                key={videoId}
                label={video.fileName}
                icon={shareable ? <ShareIcon fontSize="small" /> : undefined}
                onClick={
                  shareable ? () => onShareVideo?.(videoId) : undefined
                }
                onDelete={() => onRemove(videoId)} // Call the onRemove callback with video ID
                deleteIcon={<CloseIcon fontSize="small" />}
                sx={{
                  maxWidth: 231,
                  height: "auto",
                  cursor: shareable ? "pointer" : "default",
                  "& .MuiChip-label": {
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    py: 0.5,
                  },
                  backgroundColor: "background.paper",
                  border: "1px solid",
                  borderColor: "divider",
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                }}
              />
            );

            return shareable ? (
              <Tooltip key={videoId} title="Share this video" arrow>
                {chip}
              </Tooltip>
            ) : (
              chip
            );
          })}
        </Box>
      </Stack>
    </Box>
  );
};

export default SelectedVideosList;
